import { AnalyticsConfig, EventProperties, MixpanelProvider, buildAnalyticsConfig } from '@datarails/analytics';
import type { MixpanelConfig } from '@datarails/analytics';

export interface BuildPlgAnalyticsConfigInput {
    /** Mixpanel project token. Empty string → NO Mixpanel provider is registered (analytics inert). */
    mixpanelToken: string;
    /** Hard-coded org identifier sent as the `org_id` super-property. */
    orgId: string;
    /** Angular `environment.production` flag → 'prod' | 'dev'. */
    production: boolean;
}

// Explicit events only: all autocapture + session recording OFF — `autocapture: false`
// (rather than the per-flag object form) disables every autocapture behavior, including
// rage_click/dead_click and any future flags, so nothing can silently default to `true`.
// Avoids double-instrumentation noise (Heap already autocaptures) and PII risk.
export const PLG_MIXPANEL_CONFIG: Partial<MixpanelConfig> = {
    persistence: 'localStorage',
    autocapture: false,
    // The Datarails Mixpanel project is EU data residency. Without this, the lib
    // defaults to the US cluster (api-js.mixpanel.com), which ACCEPTS events
    // (verbose status:1) but stores them in the US cluster — invisible in the EU
    // project dashboard. dr-fe-app points at the same EU host. (If a future prod
    // project uses a different residency, promote this to an env field.)
    api_host: 'https://api-eu.mixpanel.com',
};

const buildMixpanelProvider = (token: string): MixpanelProvider =>
    new MixpanelProvider({ token, config: PLG_MIXPANEL_CONFIG });

// org_id is sent as a Mixpanel super-property so every event carries it and
// can be used for breakdown/filtering without joining to the user profile.
const buildSuperProperties = (orgId: string): EventProperties => (orgId ? { org_id: orgId } : {});

export const buildPlgAnalyticsConfig = ({
    mixpanelToken,
    orgId,
    production,
}: BuildPlgAnalyticsConfigInput): AnalyticsConfig =>
    buildAnalyticsConfig({
        surface: 'web',
        environment: { environment: production ? 'prod' : 'dev' },
        source: 'plg',
        superProperties: buildSuperProperties(orgId),
        // Register the Mixpanel provider ONLY when a token is present. With an empty
        // token we register no provider, so init/track are clean no-ops rather than
        // forwarding to an uninitialized mixpanel-browser (which throws pre-init and
        // would only be swallowed by the SDK's dispatch-catch). This makes the
        // "disabled until token provisioned" contract explicit, not emergent.
        providers: mixpanelToken ? [buildMixpanelProvider(mixpanelToken)] : [],
    });
